import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { eliminarOSuspender } from '@/lib/destruccion';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import { Tag, Plus, Pencil, Trash2, ChevronDown, ChevronUp, Loader2, ArrowRight, Sparkles, Link2 } from 'lucide-react';

export default function BrandsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [subcampaigns, setSubcampaigns] = useState([]);   // ai_independent_campaigns (cerebros IA)
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', is_active: true });
  const [selProd, setSelProd] = useState({});
  const [asignando, setAsignando] = useState('');

  const load = useCallback(async () => {
    try {
      const [b, p, s] = await Promise.all([
        api.get('/brands'),
        api.get('/products').catch(() => ({ data: [] })),
        api.get('/ai-independent-campaigns').catch(() => ({ data: [] })),
      ]);
      setBrands(b.data); setProducts(p.data);
      setSubcampaigns(Array.isArray(s.data) ? s.data : []);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const id8 = (id) => id ? `#${id.substring(0, 8)}` : '';
  const getBrandName = (bid) => (brands.find(x => x.id === bid) || {}).name || 'otra marca';

  const save = async () => {
    try {
      if (editing) { await api.put(`/brands/${editing.id}`, form); }
      else { await api.post('/brands', form); }
      toast.success(editing ? 'Actualizada' : 'Creada');
      setDialog(false); setEditing(null); setForm({ name: '', description: '', is_active: true }); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const remove = async (id) => {
    await eliminarOSuspender(`/brands/${id}`, {
      etiqueta: 'Marca',
      confirmar: '¿Retirar esta marca de la operación?',
      alTerminar: load,
    });
  };

  const asignarProducto = async (brandId, productId) => {
    if (!productId) return;
    setAsignando(brandId);
    try {
      await api.put(`/products/${productId}`, { brand_id: brandId });
      toast.success('Producto asignado a la marca');
      setSelProd(prev => ({ ...prev, [brandId]: '' }));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo asignar'); }
    finally { setAsignando(''); }
  };

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-6" data-testid="brands-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2"><Tag className="w-5 h-5 text-violet-400" />Marcas</h1>
          <p className="text-xs text-muted-foreground mt-1">{brands.length} marcas configuradas</p>
        </div>
        <Button size="sm" className="text-xs" onClick={() => { setEditing(null); setForm({ name: '', description: '', is_active: true }); setDialog(true); }} data-testid="new-brand-btn"><Plus className="w-3.5 h-3.5 mr-1" />Nueva Marca</Button>
      </div>

      {brands.length === 0 ? (
        <Card className="bg-card/50 backdrop-blur-md border-white/5"><CardContent className="py-12 text-center space-y-3">
          <Tag className="w-10 h-10 text-muted-foreground mx-auto" />
          <p className="text-sm text-white">Aun no tienes marcas.</p>
          <p className="text-xs text-muted-foreground">Crea tu primera marca para agrupar productos.</p>
          <Button size="sm" className="text-xs" onClick={() => setDialog(true)}>Crear primera marca</Button>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {brands.map(b => {
            const brandProducts = products.filter(p => p.brand_id === b.id);
            const brandSubcampaigns = subcampaigns.filter(s => s.brand_id === b.id);
            const asignables = products.filter(p => p.brand_id !== b.id);
            const isOpen = expanded[b.id];
            return (
              <Card key={b.id} className="bg-card/50 backdrop-blur-md border-white/5" data-testid={`brand-card-${b.id}`}>
                <CardContent className="p-0">
                  <div className="flex items-center justify-between px-4 py-3 cursor-pointer" onClick={() => toggle(b.id)}>
                    <div className="space-y-0.5">
                      <p className="text-sm font-medium text-white">{b.name}</p>
                      <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                        <span>{id8(b.id)}</span>
                        {b.description && <span>{b.description}</span>}
                        <span>{brandProducts.length} productos</span>
                        <span>{brandSubcampaigns.length} cerebro(s) IA</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`text-[9px] ${b.is_active ? 'text-emerald-400 border-emerald-500/20' : 'text-muted-foreground border-white/10'}`}>{b.is_active ? 'Activa' : 'Inactiva'}</Badge>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={e => { e.stopPropagation(); setEditing(b); setForm({ name: b.name, description: b.description || '', is_active: b.is_active }); setDialog(true); }}><Pencil className="w-3 h-3" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={e => { e.stopPropagation(); remove(b.id); }}><Trash2 className="w-3 h-3" /></Button>
                      {isOpen ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                    </div>
                  </div>
                  {isOpen && (
                    <div className="px-4 pb-3 pt-1 border-t border-white/5 space-y-2">
                      <p className="text-[10px] font-medium text-white/60">Productos de esta marca:</p>
                      {brandProducts.length === 0 ? (
                        <p className="text-[10px] text-muted-foreground italic">Sin productos vinculados</p>
                      ) : brandProducts.map(p => (
                        <div key={p.id} className="flex items-center justify-between px-3 py-2 rounded bg-white/[0.02] border border-white/5">
                          <div>
                            <p className="text-xs text-white">{p.name}</p>
                            <p className="text-[9px] text-muted-foreground">{id8(p.id)}</p>
                          </div>
                          <Button variant="ghost" size="sm" className="h-6 text-[9px] text-teal-400" onClick={() => navigate('/products')} data-testid={`go-product-${p.id}`}><ArrowRight className="w-3 h-3 mr-1" />Ver</Button>
                        </div>
                      ))}

                      {isAdmin && (
                        <div className="mt-2">
                          {asignables.length === 0 ? (
                            <p className="text-[10px] text-muted-foreground italic">Todos tus productos ya están en una marca. Crea más en Productos y Servicios.</p>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Select value={selProd[b.id] || ''} onValueChange={(v) => setSelProd(prev => ({ ...prev, [b.id]: v }))}>
                                <SelectTrigger className="h-7 text-[11px] bg-black/20 border-white/10 flex-1" data-testid={`assign-select-${b.id}`}><SelectValue placeholder="Asignar un producto a esta marca…" /></SelectTrigger>
                                <SelectContent>
                                  {asignables.map(p => (
                                    <SelectItem key={p.id} value={p.id} className="text-[11px]">{p.name}{p.brand_id ? ` — mover de ${getBrandName(p.brand_id)}` : ' — sin marca'}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Button size="sm" className="h-7 text-[10px] gap-1" disabled={!selProd[b.id] || asignando === b.id} onClick={() => asignarProducto(b.id, selProd[b.id])} data-testid={`assign-btn-${b.id}`}>
                                {asignando === b.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Link2 className="w-3 h-3" />}Asignar
                              </Button>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Cerebro de IA (subcampañas) de esta marca = ai_independent_campaigns con brand_id = b.id.
                          La ASOCIACIÓN se hace en Configuración → Asistente IA → Campañas. Aquí solo se muestra. */}
                      <div className="mt-3 pt-3 border-t border-white/5">
                        <p className="text-[10px] font-medium text-white/60 mb-2">Cerebro de IA de esta marca:</p>
                        {brandSubcampaigns.length === 0 ? (
                          <p className="text-[10px] text-muted-foreground italic">
                            Esta marca usa el <span className="text-white/70">Asistente de tu empresa</span> (cerebro por defecto). Para darle un cerebro especializado, ve a{' '}
                            <button className="text-teal-400 underline" onClick={() => navigate('/settings?tab=ia&sub=campanas')} data-testid={`go-ia-${b.id}`}>Configuración → Asistente IA → Campañas</button>{' '}y asígnalo a esta marca.
                          </p>
                        ) : (
                          <>
                            <p className="text-[9px] text-muted-foreground mb-1.5">{brandSubcampaigns.length} cerebro(s) especializado(s) atienden esta marca. Se configuran en Asistente IA → Campañas.</p>
                            {brandSubcampaigns.map(sc => (
                              <div key={sc.id} className="flex items-center gap-2 rounded bg-white/[0.02] border border-white/5 px-2.5 py-1.5 mb-1" data-testid={`brand-subcampaign-${sc.id}`}>
                                <Sparkles className="w-3 h-3 text-violet-400 shrink-0" />
                                <span className="text-[10px] text-white truncate flex-1">{sc.name}</span>
                                <Badge variant="outline" className={`text-[8px] shrink-0 ${sc.is_active ? 'text-emerald-400 border-emerald-500/20' : 'text-muted-foreground border-white/10'}`}>{sc.is_active ? 'Activo' : 'Inactivo'}</Badge>
                                <Button variant="ghost" size="sm" className="h-5 text-[9px] text-teal-400 px-1.5 shrink-0" onClick={() => navigate('/settings?tab=ia&sub=campanas')} data-testid={`config-subcampaign-${sc.id}`}>Configurar</Button>
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-[#0f1117] border-white/10 text-white max-w-sm">
          <DialogHeader><DialogTitle className="text-sm">{editing ? 'Editar Marca' : 'Nueva Marca'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Nombre *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1" data-testid="brand-name" /></div>
            <div><Label className="text-xs">Descripcion</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="bg-black/20 border-white/10 text-xs mt-1" /></div>
            <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={v => setForm({...form, is_active: v})} /><Label className="text-xs">Activa</Label></div>
            <Button size="sm" className="w-full text-xs" onClick={save} data-testid="brand-save">Guardar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
